// pkg72a — green→navy conversion: decorative dark-green surfaces (#1a3a2a/#2a5a4a — banner gradients, featured borders, buttons, toggles, tabs) become NAVY_DEEP #0F1838 → NAVY #1E2761; SEMANTIC greens kept (status/success badges, category chip triads, ✓ text); selected-module card converts as a pair (navy border + pale-navy #F0F2F8 tint) — delivered 2026-07-12
// pkg72 — LawMighty retheme: brand hex sweep (#1a1a1a→#0F1838 NAVY_DEEP, #0f0f0f→#0A1028, #f8f8f8→#F5F1E8 CREAM, #c0392b→#B85042 TERRA) — delivered 2026-07-12
// pkg67c — HOTFIX: Quick Time (dashboard Log Time) computed + displayed the effective rate but never sent rate_used on save — entries billed at $0 despite the bold rate note; the payload now carries effectiveRate (fixable retroactively via inline edit on Recent Entries) — delivered 2026-07-12
// pkg51 — wheel-scroll can no longer change a number input's value (a scroll notch = one $0.01 step was how $300.00 became $299.98): every type="number" input blurs on wheel — delivered 2026-07-09
// pkg49 — rate precedence flip (D-52a): per-client biller override > CLIENT's rate > biller default — delivered 2026-07-09
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { adminApi } from '../utils/adminApi';
import { useAdminAuth } from '../contexts/AdminAuthContext';


// ─────────────────────────────────────────────────────────────────────────────
// Master Log Time page.
//
// Same time-entry form that lives on each client's Billing tab, but exposed
// at the top level so a biller can quickly log time without navigating to
// the matter first. The client selector is required; once chosen, the biller
// dropdown filters to that client's team and the rate auto-displays.
//
// "Rapid-fire mode" (default after the first save): keeps the client and
// biller, clears date/description/hours, focuses the description field.
// "Switch Client" button clears everything and refocuses the picker.
// ─────────────────────────────────────────────────────────────────────────────

const th = {
  padding: '8px 12px',
  textAlign: 'left',
  fontSize: 11,
  fontFamily: 'Arial, sans-serif',
  textTransform: 'uppercase',
  letterSpacing: '0.04em',
  color: '#fff',
  fontWeight: 600,
  background: '#0F1838'
};
const td = {
  padding: '8px 12px',
  fontSize: 13,
  fontFamily: 'Arial, sans-serif',
  borderBottom: '1px solid #eee',
  verticalAlign: 'top'
};

function fmtMoney(n) {
  const num = parseFloat(n) || 0;
  return '$' + num.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
function fmtIsoDate(iso) {
  if (!iso) return '—';
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(iso));
  if (!m) return iso;
  const [, yr, mo, dy] = m;
  return `${MONTHS[parseInt(mo, 10) - 1]} ${parseInt(dy, 10)}, ${yr}`;
}

function todayIso() {
  return new Date().toISOString().slice(0, 10);
}

export default function AdminQuickTimePage() {
  const navigate = useNavigate();
  const { adminUser } = useAdminAuth();


  const [clients, setClients] = useState([]);
  const [loadingClients, setLoadingClients] = useState(true);
  const [clientFilter, setClientFilter] = useState('');
  const [selectedClientId, setSelectedClientId] = useState('');

  // Per-client billing context (team, rate overrides, recent entries)
  const [billing, setBilling] = useState(null);
  const [loadingBilling, setLoadingBilling] = useState(false);

  // Form state
  const [entryDate, setEntryDate] = useState(todayIso());
  const [hours, setHours] = useState('');
  const [description, setDescription] = useState('');
  const [billable, setBillable] = useState(true);
  const [billedBy, setBilledBy] = useState('');

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Inline-edit state for a row in the "Recent Entries" table. Only one row
  // can be edited at a time. Entries locked to an invoice can't be edited
  // here — the row shows an Invoiced badge with no edit/del controls.
  // `_rate_touched` mirrors the convention on the Client Detail page: when
  // true, switching billers will NOT overwrite the rate field.
  const [editingEntryId, setEditingEntryId] = useState(null);
  const [editEntry, setEditEntry] = useState({ entry_date: '', description: '', units: '', billable: true, billed_by: '', rate_used: '', _rate_touched: false });
  const [savingEditEntry, setSavingEditEntry] = useState(false);

  const descriptionRef = useRef(null);
  const clientPickerRef = useRef(null);

  // ── Load all clients on mount ─────────────────────────────────────────────
  useEffect(() => {
    let cancelled = false;
    adminApi.listClients().
    then((rows) => {if (!cancelled) {setClients(rows || []);setLoadingClients(false);}}).
    catch((err) => {if (!cancelled) {setError(err.message);setLoadingClients(false);}});
    return () => {cancelled = true;};
  }, []);

  // ── Fetch the selected client's billing data when client changes ─────────
  useEffect(() => {
    if (!selectedClientId) {
      setBilling(null);
      setBilledBy('');
      return;
    }
    let cancelled = false;
    setLoadingBilling(true);
    adminApi.getBilling(selectedClientId).
    then((data) => {
      if (cancelled) return;
      setBilling(data);
      // Default biller: signed-in admin if they're on the team, else first member.
      const team = data.billers || [];
      const me = team.find((b) => b.id === adminUser?.id);
      setBilledBy(me ? me.id : team[0]?.id || '');
      setLoadingBilling(false);
    }).
    catch((err) => {
      if (cancelled) return;
      setError(err.message);
      setLoadingBilling(false);
    });
    return () => {cancelled = true;};
  }, [selectedClientId, adminUser?.id]);

  // ── Filtered client list for the picker dropdown ──────────────────────────
  // Filter runs against the real underlying fields so typing still finds
  // the right client even while privacy mode is on.
  const filteredClients = useMemo(() => {
    const q = clientFilter.trim().toLowerCase();
    const sorted = [...clients].sort((a, b) => (a.name || '').localeCompare(b.name || ''));
    if (!q) return sorted;
    return sorted.filter((c) => {
      const hay = [c.name, c.email, c.username, c.clio_matter_number, c.case_description].
      filter(Boolean).join(' ').toLowerCase();
      return hay.includes(q);
    });
  }, [clients, clientFilter]);

  const selectedClient = clients.find((c) => c.id === selectedClientId);

  // Rate that will be used for this entry (matches backend logic in billing.js):
  // pkg49 (D-52a) — per-client biller override > the CLIENT's rate > biller
  // default. The client's rate is the contract rate assigned at creation and
  // must not move when a staff default changes.
  const effectiveRate = useMemo(() => {
    if (!billing || !billedBy) return null;
    const override = (billing.billerRates || []).find((r) => r.admin_user_id === billedBy);
    if (override && override.hourly_rate != null) return parseFloat(override.hourly_rate);
    if (billing.hourly_rate != null && billing.hourly_rate !== '') return parseFloat(billing.hourly_rate);
    const biller = (billing.billers || []).find((b) => b.id === billedBy);
    if (biller && biller.default_hourly_rate != null) return parseFloat(biller.default_hourly_rate);
    return null;
  }, [billing, billedBy]);

  // ── Form handlers ─────────────────────────────────────────────────────────

  function clearEntryFields() {
    setEntryDate(todayIso());
    setHours('');
    setDescription('');
    setBillable(true);
  }

  function handleSwitchClient() {
    setSelectedClientId('');
    setBilling(null);
    setBilledBy('');
    setClientFilter('');
    clearEntryFields();
    setError('');
    setSuccess('');
    setTimeout(() => clientPickerRef.current?.focus(), 50);
  }

  async function handleSave(e) {
    if (e && e.preventDefault) e.preventDefault();
    setError('');setSuccess('');

    if (!selectedClientId) {
      setError('Pick a client first.');
      return;
    }
    const hrs = parseFloat(hours);
    if (!isFinite(hrs) || hrs <= 0) {
      setError('Hours must be a positive number.');
      return;
    }
    if (!description.trim()) {
      setError('Description is required.');
      return;
    }
    if (!billedBy) {
      setError('Pick a biller (this client has no team members yet — add one on the client\'s Billing tab).');
      return;
    }

    setSaving(true);
    try {
      await adminApi.addBillingEntry(selectedClientId, {
        entry_date: entryDate,
        description: description.trim(),
        units: hrs,
        billable,
        billed_by: billedBy,
        // pkg67c — THE FIX: the page computed and displayed effectiveRate (the
        // bold "rate that will be used" note) but never sent it, so the
        // backend stored rate_used = NULL and the entry billed at $0. The
        // POST /api/billing/:clientId route stores rate_used verbatim — the
        // frontend is the rate resolver, so it must actually send the rate
        // it promises.
        rate_used: effectiveRate != null ? effectiveRate : null
      });
      // Refresh the client's billing context (recent entries list) but keep
      // the form in rapid-fire mode: client + biller stay, everything else clears.
      const refreshed = await adminApi.getBilling(selectedClientId);
      setBilling(refreshed);
      clearEntryFields();
      // Use the masked name in the success toast so the toast doesn't leak.
      const displayName = selectedClient?.name || 'client';
      setSuccess(`Logged ${hrs.toFixed(1)} hr for ${displayName}.`);
      setTimeout(() => descriptionRef.current?.focus(), 50);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  }

  // ── Inline edit handlers for the Recent Entries table ─────────────────────
  // Same conventions as the Client Detail page's billing tab: open one row at
  // a time, auto-fill rate when biller changes, PATCH on save, refresh the
  // billing context so the row re-renders with the saved values.

  function handleStartEditEntry(entry) {
    setError('');setSuccess('');
    setEditingEntryId(entry.id);
    setEditEntry({
      entry_date:  entry.entry_date ? String(entry.entry_date).slice(0, 10) : '',
      description: entry.description || '',
      units:       entry.units != null ? String(entry.units) : '',
      billable:    entry.billable !== false,
      billed_by:   entry.billed_by || '',
      rate_used:   entry.rate_used != null ? String(entry.rate_used) : '',
      _rate_touched: entry.rate_used != null,
    });
  }

  function handleCancelEditEntry() {
    setEditingEntryId(null);
    setEditEntry({ entry_date: '', description: '', units: '', billable: true, billed_by: '', rate_used: '', _rate_touched: false });
  }

  function handleEditBillerChange(billerId) {
    const rate = billing?.rateMap?.[billerId] || '';
    setEditEntry((p) => ({
      ...p,
      billed_by: billerId,
      rate_used: p._rate_touched ? p.rate_used : (rate ? String(rate) : ''),
    }));
  }

  async function handleSaveEditEntry(entryId) {
    if (!editEntry.entry_date || !editEntry.description.trim() || !editEntry.units) {
      setError('Date, description, and units are required.');
      return;
    }
    setSavingEditEntry(true);setError('');setSuccess('');
    try {
      await adminApi.updateBillingEntry(entryId, {
        entry_date:  editEntry.entry_date,
        description: editEntry.description.trim(),
        units:       parseFloat(editEntry.units),
        billable:    editEntry.billable,
        billed_by:   editEntry.billed_by || null,
        rate_used:   editEntry.rate_used === '' ? null : parseFloat(editEntry.rate_used),
      });
      // Refresh the billing context so the table shows saved values + any
      // recomputed rate. Cheaper than juggling local state.
      const refreshed = await adminApi.getBilling(selectedClientId);
      setBilling(refreshed);
      handleCancelEditEntry();
      setSuccess('Time entry updated.');
    } catch (err) {
      setError(err.message);
    } finally {
      setSavingEditEntry(false);
    }
  }

  async function handleDeleteEntry(entryId) {
    if (!window.confirm('Delete this time entry? This cannot be undone.')) return;
    setError('');setSuccess('');
    try {
      await adminApi.deleteBillingEntry(entryId);
      const refreshed = await adminApi.getBilling(selectedClientId);
      setBilling(refreshed);
      setSuccess('Time entry deleted.');
    } catch (err) {
      setError(err.message);
    }
  }

  // Recent entries for this client (last 5, sorted desc by date then created_at)
  const recentEntries = useMemo(() => {
    if (!billing) return [];
    const list = [...(billing.entries || [])].sort((a, b) => {
      if (a.entry_date < b.entry_date) return 1;
      if (a.entry_date > b.entry_date) return -1;
      return (b.created_at || '').localeCompare(a.created_at || '');
    });
    return list.slice(0, 5);
  }, [billing]);

  // ──────────────────────────────────────────────────────────────────────────

  return (
    <div>
      <h1 className="page-title">Log Time</h1>
      <p className="page-subtitle">
        Quick time entry across any client. Pick the client, log the entry, and the form keeps the client open for rapid-fire logging.
        Same form as the per-client Billing tab — entries land in the same place.
      </p>

      {error && <div className="alert alert-error">{error}</div>}
      {success && <div className="alert alert-success">{success}</div>}

      {/* Client picker */}
      <div className="section-head">
        Client {selectedClientId ? '✓' : <span style={{ color: '#B85042', fontWeight: 400 }}>(required)</span>}
      </div>
      <div className="card">
        {!selectedClientId ?
        <>
            <input
            ref={clientPickerRef}
            type="text"
            autoFocus
            value={clientFilter}
            onChange={(e) => setClientFilter(e.target.value)}
            placeholder={loadingClients ? 'Loading clients...' : 'Type to filter by name, email, matter number, or case…'}
            disabled={loadingClients}
            style={{ width: '100%', boxSizing: 'border-box' }} />
          
            {!loadingClients && filteredClients.length > 0 &&
          <div style={{ marginTop: 12, maxHeight: 320, overflowY: 'auto', border: '1px solid #eee', borderRadius: 4 }}>
                {filteredClients.map((c, i) =>
            <div key={c.id}
            onClick={() => {setSelectedClientId(c.id);setClientFilter('');}}
            style={{
              padding: '10px 12px',
              borderBottom: i === filteredClients.length - 1 ? 'none' : '1px solid #f0f0f0',
              cursor: 'pointer',
              background: i % 2 === 0 ? '#fff' : '#fafafa',
              fontFamily: 'Arial, sans-serif'
            }}
            onMouseEnter={(e) => e.currentTarget.style.background = '#eef4fb'}
            onMouseLeave={(e) => e.currentTarget.style.background = i % 2 === 0 ? '#fff' : '#fafafa'}>
                    <div style={{ fontSize: 13, fontWeight: 500 }}>{c.name}</div>
                    <div style={{ fontSize: 11, color: '#888', marginTop: 2 }}>
                      {c.clio_matter_number ? `${c.clio_matter_number} · ` : ''}
                      {c.case_description ? c.case_description : c.email ? '' : c.username ? `@${c.username}` : '—'}
                    </div>
                  </div>
            )}
              </div>
          }
            {!loadingClients && filteredClients.length === 0 &&
          <div style={{ marginTop: 12, padding: 14, textAlign: 'center', color: '#888', fontSize: 13, fontFamily: 'Arial, sans-serif' }}>
                No clients match "{clientFilter}".
              </div>
          }
          </> :

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
            <div>
              <div style={{ fontSize: 16, fontFamily: 'Arial, sans-serif', fontWeight: 600 }}>
                {selectedClient?.name}
              </div>
              <div style={{ fontSize: 12, color: '#888', fontFamily: 'Arial, sans-serif', marginTop: 2 }}>
                {selectedClient?.clio_matter_number ? `${selectedClient.clio_matter_number} · ` : ''}
                {selectedClient?.case_description ? selectedClient.case_description : '—'}
              </div>
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button className="btn btn-sm" onClick={() => navigate(`/admin/clients/${selectedClientId}?tab=billing`)}>
                Open Client →
              </button>
              <button className="btn btn-sm" onClick={handleSwitchClient}>Switch Client</button>
            </div>
          </div>
        }
      </div>

      {/* Time entry form (only when client picked) */}
      {selectedClientId &&
      <>
          <div className="section-head">Time Entry</div>
          <div className="card">
            {loadingBilling ?
          <div style={{ fontFamily: 'Arial, sans-serif', fontSize: 13, color: '#888' }}>Loading client team & rates…</div> :
          (billing?.billers || []).length === 0 ?
          <div className="alert alert-error" style={{ marginBottom: 0 }}>
                This client has no team members. Add one on the client's Billing tab before logging time.
                <div style={{ marginTop: 8 }}>
                  <button className="btn btn-sm" onClick={() => navigate(`/admin/clients/${selectedClientId}?tab=billing`)}>
                    Go to Billing Tab →
                  </button>
                </div>
              </div> :

          <form onSubmit={handleSave}>
                <div className="form-row">
                  <div className="form-group" style={{ flex: '0 0 180px' }}>
                    <label>Date</label>
                    <input type="date" value={entryDate}
                onChange={(e) => setEntryDate(e.target.value)}
                disabled={saving} required />
                  </div>
                  <div className="form-group" style={{ flex: '0 0 140px' }}>
                    <label>Hours</label>
                    <input type="number" onWheel={(e) => e.currentTarget.blur()} step="0.1" min="0" value={hours}
                onChange={(e) => setHours(e.target.value)}
                placeholder="e.g. 1.5"
                disabled={saving} required />
                  </div>
                  <div className="form-group">
                    <label>Biller</label>
                    <select value={billedBy}
                onChange={(e) => setBilledBy(e.target.value)}
                disabled={saving} required>
                      {(billing?.billers || []).map((b) =>
                  <option key={b.id} value={b.id}>{b.name}</option>
                  )}
                    </select>
                  </div>
                </div>
                <div className="form-group">
                  <label>Description</label>
                  <input ref={descriptionRef} type="text" value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="What you did"
              disabled={saving} required />
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12, marginTop: 4 }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontFamily: 'Arial, sans-serif', fontSize: 13, cursor: 'pointer' }}>
                    <input type="checkbox" checked={billable}
                onChange={(e) => setBillable(e.target.checked)} disabled={saving} />
                    Billable
                  </label>
                  <div style={{ fontFamily: 'Arial, sans-serif', fontSize: 12, color: '#555' }}>
                    Rate: <strong>{effectiveRate != null ? fmtMoney(effectiveRate) + '/hr' : 'no rate set'}</strong>
                    {effectiveRate != null && hours &&
                <> &nbsp;·&nbsp; This entry: <strong>{fmtMoney(effectiveRate * (parseFloat(hours) || 0))}</strong></>
                }
                  </div>
                </div>
                <div className="btn-row" style={{ marginTop: 16 }}>
                  <button type="button" className="btn" onClick={handleSwitchClient} disabled={saving}>
                    Switch Client
                  </button>
                  <button type="submit" className="btn btn-primary" disabled={saving}>
                    {saving ? 'Saving...' : 'Log Time'}
                  </button>
                </div>
              </form>
          }
          </div>

          {/* Recent entries for this client (context only) */}
          <div className="section-head">Recent Entries for {selectedClient?.name}</div>
          <div className="card" style={{ padding: recentEntries.length === 0 ? 16 : 0 }}>
            {recentEntries.length === 0 ?
          <p style={{ fontFamily: 'Arial, sans-serif', fontSize: 13, color: '#888', margin: 0 }}>
                No time entries on file yet for this client.
              </p> :

          <div className="table-wrap">
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr>
                      <th style={th}>Date</th>
                      <th style={th}>Biller</th>
                      <th style={th}>Description</th>
                      <th style={{ ...th, textAlign: 'right' }}>Hours</th>
                      <th style={th}>Status</th>
                      <th style={{ ...th, width: 110 }}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {recentEntries.map((e, i) => {
                const isEditing = editingEntryId === e.id;
                const onInvoice = !!e.invoice_id;
                if (isEditing) {
                  return (
                    <tr key={e.id} style={{ borderBottom: '1px solid #eee', background: '#eef4fb' }}>
                            <td style={{ ...td, padding: '10px 8px' }}>
                              <input type="date" value={editEntry.entry_date}
                          onChange={(ev) => setEditEntry((p) => ({ ...p, entry_date: ev.target.value }))}
                          disabled={savingEditEntry}
                          style={{ width: '100%', padding: '7px 8px', fontSize: 14, boxSizing: 'border-box', border: '1px solid #bbb', borderRadius: 3 }} />
                            </td>
                            <td style={{ ...td, padding: '10px 8px' }}>
                              <select value={editEntry.billed_by}
                          onChange={(ev) => handleEditBillerChange(ev.target.value)}
                          disabled={savingEditEntry}
                          style={{ width: '100%', padding: '7px 8px', fontSize: 14, boxSizing: 'border-box', border: '1px solid #bbb', borderRadius: 3 }}>
                                <option value="">—</option>
                                {(billing?.billers || []).map((b) =>
                            <option key={b.id} value={b.id}>{b.name}</option>
                            )}
                              </select>
                            </td>
                            <td style={{ ...td, padding: '10px 8px' }}>
                              <input type="text" value={editEntry.description}
                          onChange={(ev) => setEditEntry((p) => ({ ...p, description: ev.target.value }))}
                          disabled={savingEditEntry}
                          style={{ width: '100%', padding: '7px 8px', fontSize: 14, boxSizing: 'border-box', border: '1px solid #bbb', borderRadius: 3 }} />
                            </td>
                            <td style={{ ...td, padding: '10px 8px' }}>
                              <input type="number" onWheel={(e) => e.currentTarget.blur()} min="0.01" max="100" step="0.01" value={editEntry.units}
                          onChange={(ev) => setEditEntry((p) => ({ ...p, units: ev.target.value }))}
                          disabled={savingEditEntry}
                          style={{ width: '100%', padding: '7px 8px', fontSize: 14, textAlign: 'right', fontFamily: 'monospace', boxSizing: 'border-box', border: '1px solid #bbb', borderRadius: 3 }} />
                            </td>
                            <td style={{ ...td, padding: '10px 8px' }}>
                              <select value={editEntry.billable ? 'yes' : 'no'}
                          onChange={(ev) => setEditEntry((p) => ({ ...p, billable: ev.target.value === 'yes' }))}
                          disabled={savingEditEntry}
                          style={{ width: '100%', padding: '7px 8px', fontSize: 13, boxSizing: 'border-box', border: '1px solid #bbb', borderRadius: 3 }}>
                                <option value="yes">Billable</option>
                                <option value="no">Non-Bill</option>
                              </select>
                            </td>
                            <td style={{ ...td, padding: '10px 8px', textAlign: 'center', whiteSpace: 'nowrap' }}>
                              <button onClick={() => handleSaveEditEntry(e.id)}
                          disabled={savingEditEntry}
                          style={{ background: '#0F1838', border: '1px solid #0F1838', color: '#fff', borderRadius: 3, padding: '6px 12px', cursor: savingEditEntry ? 'default' : 'pointer', fontSize: 13, marginRight: 4 }}>
                                {savingEditEntry ? '…' : 'Save'}
                              </button>
                              <button onClick={handleCancelEditEntry}
                          disabled={savingEditEntry}
                          style={{ background: 'none', border: '1px solid #888', color: '#555', borderRadius: 3, padding: '6px 12px', cursor: savingEditEntry ? 'default' : 'pointer', fontSize: 13 }}>
                                Cancel
                              </button>
                            </td>
                          </tr>);

                }
                return (
                  <tr key={e.id} style={{ borderBottom: '1px solid #eee', background: i % 2 === 0 ? '#fff' : '#fafafa' }}>
                        <td style={{ ...td, whiteSpace: 'nowrap' }}>{fmtIsoDate(e.entry_date)}</td>
                        <td style={{ ...td, color: '#555' }}>
                          {(billing?.billers || []).find((b) => b.id === e.billed_by)?.name || '—'}
                        </td>
                        <td style={{ ...td, maxWidth: 360 }}>
                          <div style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {e.description ? e.description : '—'}
                          </div>
                        </td>
                        <td style={{ ...td, textAlign: 'right', fontFamily: 'monospace' }}>
                          {parseFloat(e.units || 0).toFixed(1)}
                        </td>
                        <td style={td}>
                          {e.invoice_id ?
                    <span style={{ fontSize: 11, padding: '2px 6px', borderRadius: 3, background: '#e8f0fb', color: '#2f5496', border: '1px solid #bcd4ee' }}>
                              Invoiced
                            </span> :
                    e.billable === false ?
                    <span style={{ fontSize: 11, padding: '2px 6px', borderRadius: 3, background: '#f0f0f0', color: '#888', border: '1px solid #dcdcdc' }}>
                              Non-bill
                            </span> :

                    <span style={{ fontSize: 11, padding: '2px 6px', borderRadius: 3, background: '#f0faf5', color: '#1e7e5e', border: '1px solid #b7e3cf' }}>
                              Unbilled
                            </span>
                    }
                        </td>
                        <td style={{ ...td, textAlign: 'center', whiteSpace: 'nowrap' }}>
                          {onInvoice ?
                    <span style={{ fontSize: 10, color: '#aaa' }} title="Locked — on an invoice. Void or delete the invoice to edit this entry.">🔒</span> :
                    <>
                              <button onClick={() => handleStartEditEntry(e)}
                        disabled={editingEntryId !== null}
                        title={editingEntryId !== null ? 'Finish the current edit first' : 'Edit this entry'}
                        style={{ background: 'none', border: '1px solid #2f5496', color: '#2f5496', borderRadius: 3, padding: '2px 6px', cursor: editingEntryId !== null ? 'default' : 'pointer', fontSize: 11, marginRight: 4, opacity: editingEntryId !== null ? 0.5 : 1 }}>Edit</button>
                              <button onClick={() => handleDeleteEntry(e.id)}
                        disabled={editingEntryId !== null}
                        style={{ background: 'none', border: '1px solid #B85042', color: '#B85042', borderRadius: 3, padding: '2px 6px', cursor: editingEntryId !== null ? 'default' : 'pointer', fontSize: 11, opacity: editingEntryId !== null ? 0.5 : 1 }}>Del</button>
                            </>}
                        </td>
                      </tr>);

              })}
                  </tbody>
                </table>
              </div>
          }
          </div>
          <p style={{ fontFamily: 'Arial, sans-serif', fontSize: 11, color: '#888', marginTop: 6 }}>
            Showing the most recent 5 entries. Open the client to see all entries or generate an invoice.
          </p>
        </>
      }
    </div>);

}